import React, { useState, useEffect, useRef, useCallback } from "react";
import { useExamStatus } from "../ExamStatusContext";
import { useScoreTable } from "../ScoreContext";
import withResetOnNavigation from "../withResetOnNavigation";
import questions from "../QA/Test4_q_a";
import TestContainer from "../TestContainer";
import ZoomControls from "../ZoomControls";
import "../../../css/test4.css";
import "../../../css/examplequestion.css";

const Test4 = ({ onNext }) => {
  const { examStatus } = useExamStatus();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [inputValues, setInputValues] = useState({});
  const [isTest4Started, setIsTest4Started] = useState(false);
  const [notification, setNotification] = useState("");
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [alertShown, setAlertShown] = useState(false);
  const score4 = useRef(0);
  const inputRefs = useRef([]);
  const scoreTable = useScoreTable();

  const testDuration = 180;

  const exampleQuestions = [
    { wordA: "関東地方", answer: 1, wordB: "関西地方" },
    { wordA: "チューリップ", answer: 0, wordB: "チューリップ" },
    { wordA: "5861", answer: 2, wordB: "5816" },
  ];

  const stylizeSpecificWord = (word, questionId) => {
    if (questionId === 10) {
      return word.split("").map((char, i) =>
        char === "コ" ? (
          <span key={i} style={{ fontSize: "0.5em" }}>{char}</span>
        ) : (
          <span key={i}>{char}</span>
        )
      );
    }
    return word;
  };

  useEffect(() => {
    const test4Status = localStorage.getItem("test4Status");
    if (examStatus === "検査中" && test4Status === "started") {
      localStorage.setItem("test4Status", "notStarted");
    }
  }, [examStatus]);

  useEffect(() => {
    if (inputRefs.current[currentQuestionIndex]) {
      inputRefs.current[currentQuestionIndex].focus();
    }
  }, [currentQuestionIndex]);

  const gradeTest = useCallback(() => {
    let calculatedScore = 0;

    questions.forEach((question, index) => {
      const userAnswer = parseInt(inputValues[index], 10);
      if (userAnswer === question.answer) {
        calculatedScore += 1;
      }
    });

    score4.current = calculatedScore;
    localStorage.setItem("score4", calculatedScore);

    const normalizedScore = scoreTable.scoreTable4[String(calculatedScore)] ?? 0;
    const currentQScore = parseInt(localStorage.getItem("Q_score"), 10) || 0;
    localStorage.setItem("Q_score", currentQScore + normalizedScore);

    setNotification("検査を終了します！");
    setIsTimeUp(true);

    if (onNext) onNext(); // ← ここで次の検査へ進む
  }, [inputValues, scoreTable.scoreTable4, onNext]);

  const handleStartTest = () => {
    setIsTest4Started(true);
    localStorage.setItem("test4Status", "started");
  };

  const handleInputChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (isComposing) return;
    if (alertShown) setAlertShown(false);

    setInputValues((prev) => ({ ...prev, [index]: value }));

    if (value !== "" && index === currentQuestionIndex) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleCompositionStart = (e) => {
    if (
      e.nativeEvent?.inputType === "insertCompositionText" ||
      e.nativeEvent?.code?.startsWith("Numpad")
    ) {
      return;
    }

    setIsComposing(true);

    if (!alertShown) {
      alert("日本語入力モードが有効です。半角英数字モードに切り替えてください。");
      setAlertShown(true);
      setIsComposing(false);
    }
  };

  const handleCompositionEnd = (e, index) => {
    setIsComposing(false);
    const value = e.target.value.replace(/[^0-9]/g, "");

    setInputValues((prev) => ({ ...prev, [index]: value }));

    if (value !== "" && index === currentQuestionIndex) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  if (!isTest4Started) {
    return (
      <div className="test4-container">
        <h1>検査2 - やり方の説明</h1>
        <p>
          各問題で2つの単語を比較し、何か所異なるかを判断してください。
          解答欄に異なる文字の数を入力してください。
          <br />
          解答は半角数字で入力してください。
        </p>
        <h2>例題</h2>
        <div className="example-container">
          <div className="example-questions">
            {exampleQuestions.map((ex, idx) => (
              <div key={idx} className="test4-question">
                <span className="test4-word">{ex.wordA}</span>
                <input type="text" value={ex.answer} readOnly className="test4-input example" />
                <span className="test4-word">{ex.wordB}</span>
              </div>
            ))}
          </div>
        </div>
        <button className="start-button" onClick={handleStartTest}>
          検査に進む
        </button>
      </div>
    );
  }

  const renderedQuestions = questions
    .slice(0, currentQuestionIndex + 1)
    .map((q, idx) => (
      <div key={q.id} className="test4-question">
        <p>問題 {idx + 1}</p>
        <span className="test4-word">{q.wordA}</span>
        <input
          ref={(el) => (inputRefs.current[idx] = el)}
          type="text"
          value={inputValues[idx] || ""}
          onChange={(e) => handleInputChange(e, idx)}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={(e) => handleCompositionEnd(e, idx)}
          placeholder="解答を入力"
          className="test4-input"
        />
        <span className="test4-word">{stylizeSpecificWord(q.wordB, q.id)}</span>
      </div>
    ));

  return (
    <ZoomControls>
      <TestContainer
        title="検査2"
        isTimeUp={isTimeUp}
        notification={notification}
        renderedQuestions={renderedQuestions}
        manageTimer={true}
        gradeTest={gradeTest}
        testDuration={testDuration}
      />
    </ZoomControls>
  );
};

export default withResetOnNavigation(Test4);
